import { Controller } from '@hotwired/stimulus';

// Wraps a form rendered inside a Ui::SheetComponent.
//
// No Ruby component class and no in-branch callers: the consuming views
// (professional_experiences forms) live on the onboarding branch. Not dead code.
//
// Intercepts submit and posts via fetch so the response is parsed regardless
// of HTTP status. Existing endpoints can return either:
//
//   Success:  HTTP 200 with { data: ..., saved: true, ... } (no `errors` key)
//   Errors:   HTTP 200 OR 4xx with { errors: { field: "message", ... }, alert: "..." }
//
// Errors are rendered inline next to the matching input (looked up by
// `name="…[field]"`); the sheet stays open. On success, the sheet closes
// and a `form-component:save-success` event is dispatched on the form
// (bubbles), then by default the page reloads.
//
// To opt out of the reload (e.g. you're listening for save-success and
// updating the DOM yourself), add
//   data-form-component-reload-value="false"
// to the form element.
export default class extends Controller {
  static values = {
    reload: { type: Boolean, default: true },
    turbo: { type: Boolean, default: false }
  };

  connect() {
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleTurboSubmitEnd = this.handleTurboSubmitEnd.bind(this);

    document.addEventListener('click', this.handleTriggerClick);
    this.element.addEventListener('submit', this.handleSubmit);
    if (this.turboValue) {
      this.element.addEventListener('turbo:submit-end', this.handleTurboSubmitEnd);
    }
  }

  disconnect() {
    document.removeEventListener('click', this.handleTriggerClick);
    this.element.removeEventListener('submit', this.handleSubmit);
    if (this.turboValue) {
      this.element.removeEventListener('turbo:submit-end', this.handleTurboSubmitEnd);
    }
  }

  // Turbo mode: close the sheet once Turbo finishes the frame submission.
  // The frame swap itself is handled by Turbo; we just clean up the sheet UI.
  handleTurboSubmitEnd(event) {
    if (event.detail && event.detail.success) {
      this.closeSheet();
    }
  }

  // Sheet's Continue button has data-submits-form="<form-id>"
  handleTriggerClick(event) {
    if (!this.element.id) return;
    const trigger = event.target.closest(`[data-submits-form="${this.element.id}"]`);
    if (!trigger) return;
    event.preventDefault();
    this.submit();
  }

  submit() {
    if (typeof this.element.requestSubmit === 'function') {
      this.element.requestSubmit();
    } else {
      this.element.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  async handleSubmit(event) {
    if (this.turboValue) return;
    event.preventDefault();

    let payload = null;
    let httpOk = false;

    try {
      const response = await fetch(this.element.action, {
        method: this.resolvedMethod(),
        headers: { Accept: 'application/json' },
        body: new FormData(this.element),
        credentials: 'same-origin'
      });
      httpOk = response.ok;
      const text = await response.text();
      payload = this.parsePayload(text);
    } catch (err) {
      console.warn('[form-component] request failed', err); // eslint-disable-line no-console
      return;
    }

    // Errors come back either as { errors: {...} } in the body (often with
    // 200 OK on legacy endpoints) or as a non-2xx with no useful body.
    if (payload && payload.errors) {
      this.applyErrors(payload.errors);
      return;
    }
    if (!httpOk) {
      console.warn('[form-component] request failed without errors payload'); // eslint-disable-line no-console
      return;
    }

    this.dispatchSaveSuccess(payload || {});
  }

  resolvedMethod() {
    const override = this.element.querySelector('input[name="_method"]');
    return (override?.value || this.element.method || 'POST').toUpperCase();
  }

  dispatchSaveSuccess(payload) {
    this.clearErrors();
    this.element.dispatchEvent(new CustomEvent('form-component:save-success', {
      bubbles: true,
      detail: { payload, formValues: this.collectFormValues() }
    }));
    this.closeSheet();

    if (this.reloadValue) {
      // Wait for sheet close animation before reloading.
      setTimeout(() => window.location.reload(), 350);
    }
  }

  collectFormValues() {
    const values = {};
    new FormData(this.element).forEach((value, key) => {
      if (key === 'authenticity_token' || key === 'utf8' || key === '_method') return;
      values[key] = value;
    });
    return values;
  }

  parsePayload(data) {
    if (data == null || data === '') return null;
    if (typeof data === 'string') {
      try { return JSON.parse(data); } catch (e) { return null; }
    }
    return data;
  }

  applyErrors(errors) {
    this.clearErrors();
    Object.entries(errors).forEach(([field, message]) => {
      const input = this.findInputByField(field);
      if (!input) return;
      const wrapper = input.closest('.ui-field');
      if (!wrapper) return;

      const hint = wrapper.querySelector('.ui-hint');
      const text = hint && hint.querySelector('[data-hint-text]');
      if (!hint || !text) return;

      text.textContent = Array.isArray(message) ? message[0] : message;
      wrapper.classList.add('ui-field--error');
      wrapper.dataset.inputFieldErrorActive = 'true';
      hint.hidden = false;
    });
  }

  clearErrors() {
    this.element.querySelectorAll('[data-input-field-error-active]').forEach(wrapper => {
      wrapper.classList.remove('ui-field--error');
      delete wrapper.dataset.inputFieldErrorActive;
      const hint = wrapper.querySelector('.ui-hint');
      const text = hint && hint.querySelector('[data-hint-text]');
      if (!hint || !text) return;

      const original = wrapper.dataset.inputFieldHint || '';
      text.textContent = original;
      hint.hidden = original === '';
    });
  }

  findInputByField(field) {
    return (
      this.element.querySelector(`[name$="[${field}][]"]`) ||
      this.element.querySelector(`[name$="[${field}]"]`) ||
      this.element.querySelector(`[name="${field}"]`)
    );
  }

  closeSheet() {
    const sheet = this.element.closest('.ui-sheet-overlay');
    if (sheet && !sheet.hidden) {
      sheet.classList.add('ui-sheet-overlay--closing');
    }
  }
}
